import type { SxProps, Theme } from '@mui/material/styles';
import { radius } from './tokens';

/**
 * A card: raised ground, hairline border, 16px radius, the lower of the two
 * shadows. In one place because eight components share it — written out per
 * component, the eighth one is 15px of radius and nobody can say why.
 *
 * **16px everywhere, including where the designs say 20.** The L2 process cards
 * carry `radius/xl` and every other card `radius/lg`, on the same page, with
 * nothing explaining the difference. Treated as drift.
 */
export const cardSurfaceSx = {
  backgroundColor: 'surface.raised',
  border: 1,
  borderColor: 'border.default',
  borderRadius: `${String(radius.lg)}px`,
  boxShadow: 1,
} satisfies SxProps<Theme>;

/**
 * What a card that leads somewhere adds: it lifts on hover and its border firms
 * up.
 *
 * **The focus ring is deliberately not here.** The app has one, on
 * `*:focus-visible` in the baseline; the design's per-card "3px outer glow"
 * would be a second answer to the same question.
 *
 * `@media (hover: hover)` keeps the lift off touch, where there is no hover to
 * leave and the transform sticks after a tap.
 */
export const interactiveCardSx = {
  transition: 'transform 200ms ease-out, box-shadow 200ms ease-out, border-color 200ms ease-out',
  '@media (hover: hover)': {
    '&:hover': {
      borderColor: 'border.strong',
      boxShadow: 2,
      transform: 'translateY(-2px)',
    },
  },
  // The border and shadow still change, which is the part that says "this is
  // the one you are pointing at". Only the movement goes.
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'box-shadow 200ms ease-out, border-color 200ms ease-out',
    '&:hover': { transform: 'none' },
  },
} satisfies SxProps<Theme>;

/**
 * For a card whose stage-coloured edge sits flush against its top corners
 * (`PhaseCard`, `PhaseCardCrossCutting` — both clip it there with
 * `overflow: hidden`), not for one where it's inset in the padding
 * (`MethodCard`): drops `cardSurfaceSx`'s border on that one edge.
 *
 * **The stage colour already clears WCAG 1.4.11 there on its own** — every
 * `stage.*.onSurface` reads 4.5:1–6.1:1 on white, well past the 3:1 a boundary
 * needs — so a second, accessibility-driven grey line right next to a thin
 * colour bar was two markers competing for the same corner, not two people's
 * problems solved. The other three sides have nothing else marking them and
 * keep the full border.
 */
export const flushStageEdgeCardSx = {
  borderTopWidth: 0,
} satisfies SxProps<Theme>;

/**
 * What a card confirms on press: a firmer, 2px border in the phase's own
 * stage colour, on a slightly recessed ground.
 *
 * **A function, not a static object** — unlike `cardSurfaceSx` and
 * `interactiveCardSx`, this one needs the caller's own `stageColourFor(...)`
 * result, the same value already driving that card's edge or count text.
 *
 * **The ground is `surface.sunken`, not a fourth near-white grey.** The
 * spec calls for `#F8F8F8`, a value close enough to `surface.sunken`
 * (`#f1f3f5`) that a separate token would only add a barely-distinguishable
 * step between `raised` and `sunken`.
 *
 * **The 2px width applies on every side**, including a `flushStageEdgeCardSx`
 * card's top edge — pressed and the flush edge are never in tension the way
 * the resting border was: both are the same stage colour here, not a grey
 * competing with one.
 */
export const pressedCardSx = (stageColour: string) =>
  ({
    '&:active': {
      backgroundColor: 'surface.sunken',
      borderColor: stageColour,
      borderWidth: 2,
    },
  }) satisfies SxProps<Theme>;
